/**
 * Card Component with press states and variants
 */

import { forwardRef } from 'react'
import { cn } from '@/lib/utils'

interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: 'default' | 'elevated' | 'ghost' | 'interactive' | 'highlight'
  padding?: 'none' | 'sm' | 'md' | 'lg'
}

export const Card = forwardRef<HTMLDivElement, CardProps>(
  (
    {
      className,
      variant = 'default',
      padding = 'md',
      children,
      ...props
    },
    ref
  ) => {
    const baseClasses = 'rounded-xl transition-all duration-200'

    const variantClasses = {
      default: 'bg-surface shadow-card',
      elevated: 'bg-elevated shadow-elevated',
      ghost: 'bg-transparent',
      interactive:
        'bg-surface shadow-card cursor-pointer hover:bg-elevated hover:shadow-elevated active:scale-[0.98] active:shadow-card',
      highlight: 'bg-primary/5 border border-primary/20 shadow-card',
    }

    const paddingClasses = {
      none: '',
      sm: 'p-3',
      md: 'p-4',
      lg: 'p-6',
    }

    return (
      <div
        ref={ref}
        className={cn(
          baseClasses,
          variantClasses[variant],
          paddingClasses[padding],
          className
        )}
        {...props}
      >
        {children}
      </div>
    )
  }
)

Card.displayName = 'Card'

// Card Header
interface CardHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  action?: React.ReactNode
}

export function CardHeader({ className, children, action, ...props }: CardHeaderProps) {
  return (
    <div
      className={cn('flex items-center justify-between mb-3', className)}
      {...props}
    >
      <div className="flex-1">{children}</div>
      {action && <div className="flex-shrink-0 ml-3">{action}</div>}
    </div>
  )
}

// Card Title
export function CardTitle({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLHeadingElement>) {
  return (
    <h3
      className={cn('text-lg font-semibold text-text-primary', className)}
      {...props}
    >
      {children}
    </h3>
  )
}

// Card Description
export function CardDescription({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLParagraphElement>) {
  return (
    <p className={cn('text-sm text-text-muted', className)} {...props}>
      {children}
    </p>
  )
}

// Card Content
export function CardContent({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn('', className)} {...props}>
      {children}
    </div>
  )
}

// Card Footer
export function CardFooter({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn('flex items-center mt-4 pt-4 border-t border-border', className)}
      {...props}
    >
      {children}
    </div>
  )
}

// Stat Card - common pattern for metrics
interface StatCardProps {
  label: string
  value: React.ReactNode
  trend?: React.ReactNode
  icon?: React.ReactNode
  className?: string
}

export function StatCard({ label, value, trend, icon, className }: StatCardProps) {
  return (
    <Card className={cn('relative overflow-hidden', className)}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-text-muted mb-1">{label}</p>
          <div className="text-2xl font-bold text-text-primary">{value}</div>
          {trend && <div className="mt-1">{trend}</div>}
        </div>
        {icon && (
          <div className="p-2 bg-primary/10 rounded-lg text-primary">{icon}</div>
        )}
      </div>
    </Card>
  )
}

// Bento Card - for dashboard grid
interface BentoCardProps extends CardProps {
  span?: 'single' | 'double' | 'full'
  height?: 'auto' | 'sm' | 'md' | 'lg'
}

export function BentoCard({
  className,
  span = 'single',
  height = 'auto',
  children,
  ...props
}: BentoCardProps) {
  const spanClasses = {
    single: '',
    double: 'md:col-span-2',
    full: 'col-span-full',
  }

  const heightClasses = {
    auto: '',
    sm: 'min-h-[120px]',
    md: 'min-h-[180px]',
    lg: 'min-h-[240px]',
  }

  return (
    <Card
      className={cn(spanClasses[span], heightClasses[height], className)}
      {...props}
    >
      {children}
    </Card>
  )
}
